import { useCallback } from 'react';
import { Document, DocumentStatus } from '../models/knowledgeBase/Document';
import { KnowledgeBaseApi } from '../api/KnowledgeBaseApi';
import { usePolling } from './usePolling';
import { PollingConfig } from '../types/polling';
import { POLLING_CONFIGS } from '../config/polling';

const DEFAULT_CONFIG: PollingConfig = POLLING_CONFIGS.DOCUMENT_STATUS;

interface UseDocumentStatusPollingProps {
  documents: Document[];
  tenantId: string;
  catalogId: string;
  onDocumentsUpdate: (updatedDocuments: Document[]) => void;
  config?: Partial<PollingConfig>;
}

interface UseDocumentStatusPollingReturn {
  stopPolling: () => void;
  startPolling: () => void;
  isPolling: boolean;
}

export const useDocumentStatusPolling = ({
  documents,
  tenantId,
  catalogId,
  onDocumentsUpdate,
  config = {},
}: UseDocumentStatusPollingProps): UseDocumentStatusPollingReturn => {
  const pollingConfig: PollingConfig = { ...DEFAULT_CONFIG, ...config };

  const statusFilter = useCallback(
    (doc: Document): boolean => doc.status === DocumentStatus.PROCESSING,
    []
  );

  const fetchFunction = useCallback(
    async (doc: Document): Promise<Document> => {
      if (!tenantId || !catalogId) throw new Error('Missing tenantId or catalogId');
      return KnowledgeBaseApi.getDocumentByIdAsync(tenantId, catalogId, doc.id);
    },
    [tenantId, catalogId]
  );

  const { start, stop, state } = usePolling({
    items: documents,
    config: pollingConfig,
    statusFilter,
    fetchFunction,
    onUpdate: onDocumentsUpdate,
  });

  return {
    stopPolling: stop,
    startPolling: start,
    isPolling: state.isActive,
  };
};
